/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * A block to show additional information
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.3.0
 */

import { Fragment, ReactNode } from 'react';

import { icons } from '@assets/index';
import { EColorStatus } from '@modules/app/appConstants';

import { ImgStyled } from '../Block/Img';
import {
    InfoBlockContainerErrorStyled,
    InfoBlockContainerInfoStyled,
    InfoBlockContainerStyled,
    InfoBlockContainerWarningStyled,
    InfoBlockImgContainerStyled,
    InfoBlockImgContainerSuccessBorderedStyled,
    InfoBlockImgContainerSuccessStyled,
    InfoBlockImgInfoContainerStyled,
    InfoBlockMessageContainerStyled,
} from './styled/InfoBlockStyled';

interface IInfoBlockProps {
    children: ReactNode;
    className?: string;
    colorStatus: EColorStatus;
    iconSrc?: string;
}

const InfoBlock = (props: IInfoBlockProps) => {

    const {
        children,
        className,
        colorStatus,
        iconSrc,
    } = props;

    return (
        <Fragment>
            {{
                [EColorStatus.SUCCESS]: <InfoBlockContainerStyled className={className}>
                    <InfoBlockImgContainerSuccessStyled>
                        <ImgStyled
                            className="info-icon"
                            src={iconSrc || icons.checkedIcon}
                        />
                    </InfoBlockImgContainerSuccessStyled>
                    <InfoBlockMessageContainerStyled>
                        {children}
                    </InfoBlockMessageContainerStyled>
                </InfoBlockContainerStyled>,
                [EColorStatus.SUCCESS_BORDERED]: <InfoBlockImgContainerSuccessBorderedStyled className={className}>
                    <InfoBlockImgContainerSuccessStyled>
                        <ImgStyled
                            className="info-icon"
                            src={iconSrc || icons.checkedIcon}
                        />
                    </InfoBlockImgContainerSuccessStyled>
                    <InfoBlockMessageContainerStyled>
                        {children}
                    </InfoBlockMessageContainerStyled>
                </InfoBlockImgContainerSuccessBorderedStyled>,
                [EColorStatus.WARNING]: <InfoBlockContainerWarningStyled className={className}>
                    <InfoBlockImgContainerStyled>
                        <ImgStyled
                            className="small"
                            src={iconSrc || icons.infoWarningSmallIcon}
                        />
                    </InfoBlockImgContainerStyled>
                    <InfoBlockMessageContainerStyled>
                        {children}
                    </InfoBlockMessageContainerStyled>
                </InfoBlockContainerWarningStyled>,
                [EColorStatus.ERROR]: <InfoBlockContainerErrorStyled className={className}>
                    <InfoBlockImgContainerStyled>
                        <ImgStyled
                            className="small"
                            src={iconSrc || icons.infoErrorSmallIcon}
                        />
                    </InfoBlockImgContainerStyled>
                    <InfoBlockMessageContainerStyled>
                        {children}
                    </InfoBlockMessageContainerStyled>
                </InfoBlockContainerErrorStyled>,
                [EColorStatus.INFO]: <InfoBlockContainerInfoStyled className={className}>
                    <InfoBlockImgInfoContainerStyled>
                        <ImgStyled className='container big' src={icons.ellipseInfo}>
                            <ImgStyled
                                className='small'
                                src={iconSrc || icons.infoLightBlueIcon}
                            />
                        </ImgStyled>
                    </InfoBlockImgInfoContainerStyled>
                    <InfoBlockMessageContainerStyled>
                        {children}
                    </InfoBlockMessageContainerStyled>
                </InfoBlockContainerInfoStyled>,
            }[colorStatus]}
        </Fragment>
    );
};

export default InfoBlock;
